import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where, limit as qlimit } from 'firebase/firestore';
import { db } from '../../lib/firebase.js';
import type { Tile, TileColor } from '../../lib/home-schema.js';

const PALETTE: TileColor[] = [
  'navy', 'forest', 'plum', 'gold', 'terracotta', 'sage', 'mist', 'rose', 'slate', 'amber',
];

/**
 * Generates one home-base tile per active entity, each linking to that entity's
 * documents. Powers the dynamic "Entity Documents" group — entities seeded /
 * renamed / merged in the Entities admin flow through here automatically.
 */
export function useEntityTiles(): Tile[] {
  const [tiles, setTiles] = useState<Tile[]>([]);
  useEffect(() => {
    const q = query(collection(db, 'entities'), where('status', '==', 'active'), qlimit(200));
    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((d) => {
            const data = d.data() as { legalName?: string; shortName?: string; docCount?: number };
            const name = (data.shortName || data.legalName || d.id).trim();
            return { id: d.id, name, count: data.docCount ?? 0 };
          })
          .filter((r) => r.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setTiles(
          rows.map((r, i) => ({
            id: `entity_${r.id}`,
            label: r.name,
            glyph: glyphFor(r.name),
            color: PALETTE[i % PALETTE.length]!,
            href: `/documents?entity=${encodeURIComponent(r.name)}`,
            ...(r.count > 0 ? { badge: { kind: 'static' as const, value: r.count } } : {}),
          })),
        );
      },
      () => setTiles([]),
    );
  }, []);
  return tiles;
}

function glyphFor(name: string): string {
  const parts = name.replace(/[^A-Za-z0-9 ]/g, '').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
